import { useCallback, useRef } from "react";

/**
 * @param {{ onLongPress?: (e: Event) => void, onClick?: (e: Event) => void, delay?: number, disabled?: boolean }} opts
 */
export function useLongPress({
  onLongPress,
  onClick,
  delay = 520,
  disabled = false,
} = {}) {
  const timerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      if (disabled) return;
      longPressTriggeredRef.current = false;
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        longPressTriggeredRef.current = true;
        onLongPress?.(e);
      }, delay);
    },
    [clearTimer, delay, disabled, onLongPress]
  );

  const onPointerUp = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onPointerLeave = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onPointerCancel = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const handleClick = useCallback(
    (e) => {
      if (longPressTriggeredRef.current) {
        e.preventDefault();
        e.stopPropagation();
        longPressTriggeredRef.current = false;
        return;
      }
      onClick?.(e);
    },
    [onClick]
  );

  return {
    onPointerDown,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
    onClick: handleClick,
  };
}

export default useLongPress;
